import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import * as Sentry from '@sentry/vue';
import {
  BoqPosition,
  CreateItem,
  CreateSection,
  CreateTemplate,
  UpdateItem,
  UpdateVariation,
  BulkDeleteLineItemDto,
} from '@/types/api/boqs.types';
import {
  createLineItem,
  createSection,
  deleteBoq,
  deleteBoqTemplate,
  bulkDeleteLineItem,
  deleteSection,
  exportBoq,
  saveBoqAsTemplate,
  updateLineItem,
  updateSection,
  updateStatus,
  updateVariationStatus,
} from '@/api/boqs.service';
import i18n from '@/plugins/vue-i18n';
import { selectedOrgId } from '@/common/utils/role.utils';
import { useToastStore } from '@/stores/utils/toast.store';
import { useOrgStore } from '@/stores/organisations.store';
import { useBuildBoqStore } from '@/stores/build-boq.store';
import { useAuthStore } from '@/stores/auth.store';
import { capitalizeFirstLetter } from '@/common/utils/string.utils';
import { createBoq, getBOQ, getSellingBOQ } from '@/api/site.service';
import { BOQ, BoqBody, BOQItem, BOQStatus } from '@/types/api/sites.types';
import {
  exportFile,
  findItemByPathAndName,
  updateBOQWithItems,
} from '@/common/utils/general.utils';
import { isSuccess } from '@/common/utils/http.utils';
import { analyticsService } from '@/plugins/post-hog';

const { t } = i18n.global;

// Context: the status PATCH answers with no body, so the submitter the API stamps on Draft →
// Pending is mirrored here for the review bar (DL-22); the next fetch brings the server's values.
const submittedStamp = (
  status: BOQStatus,
): Pick<BOQ, 'requestedBy' | 'requestedByName' | 'requestedAt'> => {
  if (status !== BOQStatus.pending) return {};
  const user = useAuthStore().loggedInUser;
  return {
    requestedBy: user?.id ?? null,
    requestedByName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || null,
    requestedAt: new Date().toISOString(),
  };
};

interface BOQStorageItem {
  siteId: string;
  boq: BOQ;
  lastAccessed: number;
}

/**
 * How an export ended: `refused` is an HTTP error answer, which the interceptor has already told
 * the user about; `failed` (no answer, a 429, or a file that never saved) is left to the caller to say.
 */
export type BoqExportOutcome = 'exported' | 'refused' | 'failed';

interface SellingBOQStorageItem {
  siteId: string;
  sellingBoq: BOQ;
  lastAccessed: number;
}

export const useBoqStore = defineStore('boq', {
  state: () => ({
    storedBoqs: [] as BOQStorageItem[],
    storedSellingBoqs: [] as SellingBOQStorageItem[],
    currentSiteId: '',
    itemHistoryToView: {} as BOQItem | null,
    exportedBoq: null as Blob | null,
    newItem: null as BOQItem | null,
    gridContainer: {
      width: 1633,
      scroll: 0,
      top: 0,
      scrolledToRight: false,
    },
    boqFetched: false,

    // For storing items with triggered variations for the current site
    itemsWithTriggeredVariations: [] as BOQItem[],
  }),

  getters: {
    boq: (state): BOQ => {
      const stored = state.storedBoqs.find((item) => item.siteId === state.currentSiteId);
      return stored?.boq || ({} as BOQ);
    },

    sellingBoq: (state): BOQ | null => {
      const stored = state.storedSellingBoqs.find((item) => item.siteId === state.currentSiteId);
      return stored?.sellingBoq || null;
    },

    hasBoqForSite:
      (state) =>
      (siteId: string): boolean => {
        return state.storedBoqs.some((item) => item.siteId === siteId);
      },

    hasSellingBoqForSite:
      (state) =>
      (siteId: string): boolean => {
        return state.storedSellingBoqs.some((item) => item.siteId === siteId);
      },

    getBoqForSite:
      (state) =>
      (siteId: string): BOQ | null => {
        const stored = state.storedBoqs.find((item) => item.siteId === siteId);
        return stored?.boq || null;
      },

    getSellingBoqForSite:
      (state) =>
      (siteId: string): BOQ | null => {
        const stored = state.storedSellingBoqs.find((item) => item.siteId === siteId);
        return stored?.sellingBoq || null;
      },
  },

  actions: {
    // Set the current site ID to determine which BOQ to display
    setCurrentSiteId(siteId: string) {
      this.currentSiteId = siteId;

      // Update last accessed time for the current site's BOQs
      const boqItem = this.storedBoqs.find((item) => item.siteId === siteId);
      if (boqItem) {
        boqItem.lastAccessed = Date.now();
      }

      const sellingBoqItem = this.storedSellingBoqs.find((item) => item.siteId === siteId);
      if (sellingBoqItem) {
        sellingBoqItem.lastAccessed = Date.now();
      }
    },

    /**
     * Drops a site's cached bill, so its next `getBOQ` reads it fresh rather than re-reading it now.
     * The bill the store points at stays: something on screen may be reading it through `boq`.
     *
     * `includeCurrent` drops that one too, for a caller that knows no BOQ is on screen (the bulk
     * order hub). `boq` then reads empty until the BOQ page's own read lands, which it makes because
     * the site no longer has a bill here.
     */
    forgetBoq(siteId: string, { includeCurrent = false }: { includeCurrent?: boolean } = {}) {
      if (siteId === this.currentSiteId && !includeCurrent) return;

      this.storedBoqs = this.storedBoqs.filter((item) => item.siteId !== siteId);
    },

    // Store BOQ with site ID, maintaining max 2 BOQs
    storeBoq(siteId: string, boq: BOQ) {
      const existingIndex = this.storedBoqs.findIndex((item) => item.siteId === siteId);

      if (existingIndex === -1) {
        // Add new BOQ
        if (this.storedBoqs.length >= 2) {
          // Remove the oldest accessed BOQ
          const oldestIndex = this.storedBoqs.reduce(
            (oldest, current, index) =>
              current.lastAccessed < (this.storedBoqs[oldest]?.lastAccessed ?? Infinity)
                ? index
                : oldest,
            0,
          );
          this.storedBoqs.splice(oldestIndex, 1);
        }

        this.storedBoqs.push({
          siteId,
          boq,
          lastAccessed: Date.now(),
        });
      } else {
        // Update existing BOQ
        this.storedBoqs[existingIndex] = {
          siteId,
          boq,
          lastAccessed: Date.now(),
        };
      }
    },

    // Store selling BOQ with site ID, maintaining max 2 selling BOQs
    storeSellingBoq(siteId: string, sellingBoq: BOQ) {
      const existingIndex = this.storedSellingBoqs.findIndex((item) => item.siteId === siteId);

      if (existingIndex === -1) {
        // Add new selling BOQ
        if (this.storedSellingBoqs.length >= 2) {
          // Remove the oldest accessed selling BOQ
          const oldestIndex = this.storedSellingBoqs.reduce(
            (oldest, current, index) =>
              current.lastAccessed < (this.storedSellingBoqs[oldest]?.lastAccessed ?? Infinity)
                ? index
                : oldest,
            0,
          );
          this.storedSellingBoqs.splice(oldestIndex, 1);
        }

        this.storedSellingBoqs.push({
          siteId,
          sellingBoq,
          lastAccessed: Date.now(),
        });
      } else {
        this.storedSellingBoqs[existingIndex] = {
          siteId,
          sellingBoq,
          lastAccessed: Date.now(),
        };
      }
    },

    removeBoqFromStorage(siteId: string) {
      const index = this.storedBoqs.findIndex((item) => item.siteId === siteId);
      if (index !== -1) {
        this.storedBoqs.splice(index, 1);
      }
    },

    removeSellingBoqFromStorage(siteId: string) {
      const index = this.storedSellingBoqs.findIndex((item) => item.siteId === siteId);
      if (index !== -1) {
        this.storedSellingBoqs.splice(index, 1);
      }
    },

    clearAllBoqs() {
      this.storedBoqs = [];
      this.storedSellingBoqs = [];
      this.currentSiteId = '';
    },

    async createBoq(id: string, data: BoqBody) {
      const toastStore = useToastStore();

      const response = await createBoq(id, data);
      const createdBoq = response.data.data;

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast('success', t('sites.boq.created.title'));

      this.storeBoq(id, createdBoq);
      this.setCurrentSiteId(id);

      analyticsService.capture('boq_created', {
        org_id: selectedOrgId.value!,
        boq_id: createdBoq.id,
        project_id: createdBoq.projectId,
        site_id: createdBoq.siteId,
      });
    },

    async getBOQ(id: string, forceRefresh = false) {
      // Check if BOQ already exists and force refresh is not requested
      if (!forceRefresh && this.hasBoqForSite(id)) {
        this.setCurrentSiteId(id);
        return this.getBoqForSite(id);
      }

      const response = await getBOQ(id);
      const boq = response.data.data;

      this.storeBoq(id, boq);
      this.setCurrentSiteId(id);

      return boq;
    },

    async getSellingBOQ(id: string, forceRefresh = false) {
      // Check if selling BOQ already exists and force refresh is not requested
      if (!forceRefresh && this.hasSellingBoqForSite(id)) {
        this.setCurrentSiteId(id);
        return this.getSellingBoqForSite(id);
      }

      const response = await getSellingBOQ(id);
      const sellingBoq = response.data.data;

      this.storeSellingBoq(id, sellingBoq);
      this.setCurrentSiteId(id);

      return sellingBoq;
    },

    async createSection(data: CreateSection, type: 'section' | 'sub-section') {
      const toastStore = useToastStore();
      const response = await createSection(data);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast(
        'success',
        t('sites.boq.addSectionModal.addSectionSuccess.title', { type: type }),
      );

      if (this.currentSiteId) {
        this.storeBoq(this.currentSiteId, response.data.data);
      }

      analyticsService.capture(`boq_${type === 'section' ? 'section' : 'sub_section'}_created`, {
        org_id: selectedOrgId.value!,
        boq_id: response.data.data.id,
      });
    },

    async updateSection(data: CreateSection, type: 'section' | 'sub-section') {
      const toastStore = useToastStore();
      const response = await updateSection(data);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast(
        'success',
        t('sites.boq.addSectionModal.updateSectionGroupSuccess.title', {
          type: capitalizeFirstLetter(type),
        }),
      );

      if (this.currentSiteId) {
        this.storeBoq(this.currentSiteId, response.data.data);
      }

      analyticsService.capture(`boq_${type === 'section' ? 'section' : 'sub_section'}_edited`, {
        org_id: selectedOrgId.value!,
        boq_id: response.data.data.id,
      });
    },

    async deleteSection(data: BoqPosition, type: 'section' | 'sub-section') {
      const toastStore = useToastStore();
      const response = await deleteSection(data);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast(
        'success',
        t('sites.boq.deleteSectionGroupModal.deleteSectionGroupSuccess.title', {
          type: capitalizeFirstLetter(type),
        }),
      );

      if (this.currentSiteId) {
        await this.getBOQ(this.currentSiteId, true);
      }
    },

    async createLineItem(data: CreateItem, newItem?: BOQItem) {
      if (newItem) {
        this.newItem = newItem;
      }

      const response = await createLineItem(data);

      if (isSuccess(response.status)) {
        const updatedBoq = response.data.data;

        if (this.currentSiteId) {
          this.storeBoq(this.currentSiteId, updatedBoq);
        }

        this.newItem = findItemByPathAndName(
          updatedBoq.sections,
          data.position!,
          data.data.name,
          false,
          updatedBoq.status,
        );

        if (this.newItem && updatedBoq.status === BOQStatus.approved && newItem) {
          this.newItem.rate = newItem.rateVariation ?? newItem.rate;
          this.newItem.vendorItemId = newItem.specVariationId ?? newItem.vendorItemId;
          this.newItem.quantity = newItem.qtyVariation ?? newItem.quantity;
          this.newItem.unitType = newItem.unitTypeVariation ?? newItem.unitType;
          this.newItem.type = newItem.typeVariation ?? newItem.type;
          this.newItem.newItemVariationCreated = true;
          if (newItem.refVariation) {
            this.newItem.reference = newItem.refVariation;
          }
        }

        analyticsService.capture('boq_line_item_added', {
          org_id: selectedOrgId.value!,
          boq_id: response.data.data.id,
        });
      }
    },

    restoreTriggeredVariations() {
      const updatedBoq = this.getBoqForSite(this.currentSiteId);

      if (updatedBoq) {
        updateBOQWithItems(updatedBoq, this.itemsWithTriggeredVariations);
      }
    },

    async updateLineItem(data: UpdateItem, singleField?: boolean) {
      const response = await updateLineItem(data);

      if (!isSuccess(response.status) || singleField) {
        return;
      }

      if (this.currentSiteId) {
        this.storeBoq(this.currentSiteId, response.data.data);

        this.restoreTriggeredVariations();
      }
      analyticsService.capture('boq_line_item_updated', {
        org_id: selectedOrgId.value!,
        boq_id: response.data.data.id,
      });
    },

    async bulkDeleteLineItems(boqId: string, data: BulkDeleteLineItemDto[]) {
      const toastStore = useToastStore();
      const buildBoqStore = useBuildBoqStore();

      const response = await bulkDeleteLineItem(boqId, data);

      if (isSuccess(response.status)) {
        toastStore.showToast(
          'success',
          t('sites.boq.deleteLineItemModal.deleteLineItemSuccess.title'),
        );

        // Remove deleted items from selected items if included in deletion
        const deletedItemIds = data.map((item) => item.id);
        if (deletedItemIds.length > 0) {
          buildBoqStore.removeSelectedItemsById(deletedItemIds);
        }

        if (this.currentSiteId) {
          this.storeBoq(this.currentSiteId, response.data.data);
        }
      }
    },

    async updateBoqStatus(id: string, status: BOQStatus, successCopy: string, reviewNote?: string) {
      const toastStore = useToastStore();
      const response = await updateStatus(id, status, reviewNote);
      if (isSuccess(response.status)) {
        toastStore.showToast('success', t('sites.boq.statuses.toast.title'), successCopy);

        // Context: the user can change site while the request is in flight, so the answer goes onto
        // the bill it was sent for, never onto whichever bill is current when it lands (PLE-1777).
        const sentFor = this.storedBoqs.find((item) => item.boq.id === id);
        if (sentFor) {
          this.storeBoq(sentFor.siteId, { ...sentFor.boq, status, ...submittedStamp(status) });
        }

        let eventName = '';
        switch (status) {
          case BOQStatus.pending: {
            eventName = 'boq_submitted';
            break;
          }
          case BOQStatus.approved: {
            eventName = 'boq_approved';
            break;
          }
          case BOQStatus.draft: {
            eventName = 'boq_declined';
            break;
          }
        }

        if (eventName) {
          analyticsService.capture(eventName, {
            org_id: selectedOrgId.value!,
            boq_id: id,
          });
        }
      }
    },

    async saveBoqAsTemplate(data: CreateTemplate) {
      const toastStore = useToastStore();
      const orgStore = useOrgStore();
      const response = await saveBoqAsTemplate(data);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast(
        'success',
        t('sites.boq.saveAsTemplateModal.saveAsTemplateSuccess.title'),
      );
      if (selectedOrgId.value) {
        await orgStore.getBoqTemplates(selectedOrgId.value);
      }
    },

    async deleteBoqTemplate(id: string) {
      const orgStore = useOrgStore();
      const toastStore = useToastStore();
      const response = await deleteBoqTemplate(id);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast(
        'success',
        t('sites.boq.deleteTemplateModal.deleteTemplateSuccess.title'),
      );
      orgStore.templates = orgStore.templates.filter((t) => t.id !== id);
    },

    async updateVariation(data: UpdateVariation) {
      const toastStore = useToastStore();
      const response = await updateVariationStatus(data);

      if (!isSuccess(response.status)) {
        return;
      }

      analyticsService.capture(
        `boq_line_item_variation_${data.data.status === 'Approved' ? 'approved' : 'declined'}`,
        {
          org_id: selectedOrgId.value!,
          boq_id: data.id,
        },
      );

      toastStore.showToast(
        'success',
        t('sites.boq.updateVariationStatusModal.updateVariationStatusSuccess.title', {
          update: data.data.status.toLowerCase(),
        }),
      );

      if (!this.currentSiteId) {
        return;
      }

      this.storeBoq(this.currentSiteId, response.data.data);

      this.restoreTriggeredVariations();
    },

    async exportBoq(id: string, selling: boolean, siteName: string): Promise<BoqExportOutcome> {
      try {
        const response = await exportBoq(id, selling);

        this.exportedBoq = new Blob([response.data], {
          type: response.data.type,
        });

        exportFile(this.exportedBoq, response.data.type, `${siteName} - BOQ`);

        return 'exported';
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        // Context: the interceptor toasts every HTTP refusal of the export except a 429.
        if (status === 429) return 'failed';
        if (status !== undefined) return 'refused';

        // The error itself is not sent: an axios one carries the request config, bearer token included.
        Sentry.captureMessage('BOQ export failed without a response', {
          level: 'error',
          tags: {
            boqId: id,
            exportType: selling ? 'selling' : 'cost',
            errorCode: isAxiosError(error) ? (error.code ?? 'unknown') : 'not-a-request-error',
          },
        });

        return 'failed';
      }
    },

    async deleteBoq(id: string) {
      const toastStore = useToastStore();
      const response = await deleteBoq(id);

      if (!isSuccess(response.status)) {
        return;
      }

      toastStore.showToast('success', t('sites.boq.deleteBOQModal.deleteBOQSuccess.title'));

      if (this.currentSiteId) {
        this.removeBoqFromStorage(this.currentSiteId);
      }
    },
    setItemHistoryToView(item: BOQItem) {
      this.itemHistoryToView = item;
    },
  },
});
